/*
 * Copyright (c) 2023 Bolmate B.V.
 */

import React, {useContext, useEffect, useRef, useState} from 'react';
import {getSearchParams, navigate, nullify, ResponseData, showDialog, showSnackbar} from '@dvrd/dvr-controls';
import {AppContext} from "../misc/appContext";
import BolReturn from "../../models/bolReturnModel";
import {createErrorMessage} from "../../util/utils";
import Returns from "./returns";
import {onSetCrumbs} from "../../util/events";
import {
    getProcessingResult,
    ProcessingResultTranslations,
    ReturnCounts,
    ReturnProcessingResult
} from "../../util/interfaces";
import {getReturnCounts, sendInvoice, sendInvoices} from "../../util/requestSender";
import ReturnDetailsController, {ReloadableActivateHandle} from "./details/returnDetailsController";

const BolReturnsLimit = 10;

export default function ReturnsController() {
    const context = useContext(AppContext);
    const {account} = context.accountContext;
    const params = getSearchParams();
    const [returns, setReturns] = useState<BolReturn[]>([]);
    const [loading, setLoading] = useState(false);
    const [page, setPage] = useState(Number(params.get('pagina') ?? 1) - 1);
    const originalSearch = params.get('zoekterm') ?? '';
    const [search, setSearch] = useState(originalSearch);
    const [status, setStatus] = useState<ReturnProcessingResult | ''>(getProcessingResult(params.get('status')) ?? '');
    const [total, setTotal] = useState(0);
    const [counts, setCounts] = useState<ReturnCounts>({
        ACCEPTED: 0,
        CANCELED: 0,
        OPEN: 0,
        REJECTED: 0
    });
    const [loadingCounts, setLoadingCounts] = useState(false);
    const debounceTimeout = useRef<number | null>(null);
    const lastSearch = useRef(originalSearch);
    const detailsRef = useRef<ReloadableActivateHandle<BolReturn>>(null);
    const returnsAbortController = useRef<AbortController | null>(null);
    const countsAbortController = useRef<AbortController | null>(null);

    function onChangeSearch(value: string) {
        clearTimeout();
        debounceTimeout.current = window.setTimeout(() => {
            if (value !== lastSearch.current) {
                lastSearch.current = value;
                setSearch(value);
                setPage(0);
            }
        }, 300);
    }

    function onEnterSearch() {
        clearTimeout();
        setParams(page, status, search);
        loadReturns();
    }

    function onChangePage(nextPage: number) {
        return function () {
            setPage(nextPage);
        }
    }

    function onClickReturn(bolReturn: BolReturn) {
        return function () {
            detailsRef.current?.activate(bolReturn);
        }
    }

    function onClickInvoice(invoiceId: string) {
        return function (evt: React.MouseEvent) {
            evt.stopPropagation();
            navigate(`/factuur-details?id=${invoiceId}`);
        }
    }

    function onClickSend(invoiceId: string) {
        return function () {
            if(!account) return;
            setLoading(true);
            sendInvoice({
                invoice_id: invoiceId, account_id: account.id, callback: (data: ResponseData) => {
                    setLoading(false);
                    if (data.success) {
                        showSnackbar('De creditfactuur is verzonden');
                        loadReturns();
                    } else
                        showDialog(createErrorMessage(data.message ?? 'Het verzenden van de creditfactuur is niet gelukt.'), 'Verzenden mislukt');
                }
            });
        }
    }

    function onChangeStatus(status: ReturnProcessingResult | '') {
        setPage(0);
        setStatus(status);
    }

    function onClickCredit(bolReturn: BolReturn, creditAll?: boolean, overrideStatus: boolean = false) {
        return function () {
            if (!overrideStatus && bolReturn.processingResult !== ReturnProcessingResult.ACCEPTED) {
                return showDialog('De retourzending is nog niet verwerkt en kan dus nog geannuleerd of geweigerd ' +
                    'worden. Wil je de bestelling toch crediteren?', 'Bestelling crediteren', ['Nee', {
                    label: 'Ja',
                    onClick: onClickCredit(bolReturn, undefined, true),
                    primary: false
                }]);
            }
            setLoading(true);
            bolReturn.createCredit(creditAll, (success: boolean, data: ResponseData) => {
                setLoading(false);
                if (success) {
                    onCreditSuccess(data.invoice_ids);
                    showSnackbar('De creditfactuur is aangemaakt.');
                } else if (data.message === 'multiple_found')
                    showDialog('Er zijn meerdere items gevonden bij deze retourbestelling. Wil je alle items ' +
                        'toevoegen aan de creditfactuur?', 'Creditfactuur aanmaken', [{
                        label: 'Nee',
                        onClick: onClickCredit(bolReturn, false, overrideStatus)
                    }, {label: 'Ja', onClick: onClickCredit(bolReturn, true, overrideStatus)}]);
                else showDialog(createErrorMessage(data.message ?? 'Het aanmaken van de creditfactuur is niet gelukt.'),
                        'Creditfactuur aanmaken mislukt');
            });
        }
    }

    function onCreditSuccess(invoiceIds?: string[]) {
        if (!invoiceIds?.length) return;
        const action = invoiceIds.length > 1 ? 'facturen zijn' : 'factuur is',
            title = invoiceIds.length > 1 ? 'facturen' : 'factuur';
        showDialog(`De credit${action} aangemaakt. Wil je deze ook direct naar de klanten versturen?`, `Credit${title} aangemaakt`, [{
            label: 'Nee',
            primary: false,
            onClick: () => {
                loadReturns()
            }
        }, {label: 'Ja', onClick: onSendCreditInvoices(invoiceIds)}]);
    }

    function onSendCreditInvoices(invoiceIds: string[]) {
        return function () {
            if(!account) return;
            setLoading(true);
            sendInvoices({
                data: {invoice_ids: invoiceIds, account_id: account.id, send_now: true},
                callback: (data: ResponseData) => {
                    if (data.success) {
                        const action = invoiceIds.length > 1 ? 'facturen zijn' : 'factuur is';
                        showSnackbar(`De ${action} verzonden`);
                        loadReturns();
                    } else {
                        const name = invoiceIds.length === 1 ? 'factuur' : 'facturen';
                        showDialog(createErrorMessage(data.message ?? `Het verzenden van de credit${name} is niet gelukt.`), 'Verzenden mislukt');
                    }
                }
            });
        }
    }

    function onClickBol() {
        window.open('https://partner.bol.com/sdd/retailer-returns/returns', '_blank');
    }

    function clearTimeout() {
        if (debounceTimeout.current !== null) {
            window.clearTimeout(debounceTimeout.current);
            debounceTimeout.current = null;
        }
    }

    function loadReturns(searchValue?: string | null, statusValue?: ReturnProcessingResult | '' | null, pageValue?: number | null) {
        if (!account) return;
        setLoading(true);
        if (statusValue === undefined) statusValue = status;
        returnsAbortController.current = BolReturn.getAll(account.id, searchValue ?? search,
            nullify(statusValue) as ReturnProcessingResult | null,
            BolReturnsLimit, pageValue ?? page, (returns: BolReturn[], success: boolean, data: ResponseData) => {
                returnsAbortController.current = null;
                setLoading(false);
                if (success) {
                    setTotal(data.total);
                    setReturns(returns);
                    for (const ret of returns)
                        detailsRef.current?.reload(ret);
                } else showDialog(createErrorMessage(data.message ?? 'Het laden van de retouren is niet gelukt.'), 'Laden mislukt');
            });
    }

    function loadCounts() {
        if (!account) return;
        setLoadingCounts(true);
        countsAbortController.current = getReturnCounts({
            data: {account_id: account.id, period_start: null, period_end: null}, callback: (data: ResponseData) => {
                countsAbortController.current = null;
                setLoadingCounts(false);
                if (data.success) setCounts(data.counts);
            }
        });
    }

    function setParams(page: number, status: ReturnProcessingResult | '', search: string) {
        const {origin, pathname} = window.location;
        let url = `${origin}${pathname}?pagina=${page + 1}`;
        if (status !== '') url += `&status=${ProcessingResultTranslations[status].toLowerCase()}`;
        if (search) url += `&zoekterm=${search}`;
        history.pushState({}, '', url);
    }

    function cleanup() {
        clearTimeout();
        returnsAbortController.current?.abort();
        countsAbortController.current?.abort();
    }

    useEffect(() => {
        onSetCrumbs([{label: 'Retouren', route: '/retouren'}]);
        return function () {
            cleanup();
        }
    }, []);

    useEffect(() => {
        loadReturns();
        setParams(page, status, search);
        return function () {
            cleanup();
        }
    }, [account, page, status, search]);

    useEffect(() => {
        loadCounts();
        return function () {
            cleanup();
        }
    }, [account]);

    return (
        <>
            <Returns onChangeSearch={onChangeSearch} onEnterSearch={onEnterSearch} onClickReturn={onClickReturn}
                     onClickInvoice={onClickInvoice} onChangePage={onChangePage} onChangeStatus={onChangeStatus}
                     onClickSend={onClickSend} onClickCredit={onClickCredit} onClickBol={onClickBol} returns={returns}
                     loading={loading} search={search} page={page} limit={BolReturnsLimit} total={total} status={status}
                     counts={counts} loadingCounts={loadingCounts}/>
            <ReturnDetailsController onClickSend={onClickSend} onClickCredit={onClickCredit} onClickBol={onClickBol}
                                     ref={detailsRef}/>
        </>
    )
}